import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { SpineClipMixSpec } from "../../spine/SpineClipMix";
import type { SpineDriver } from "../../spine/SpineDriver";
import type { SymbolBindOpts } from "../../view/grid/GridSymbolPool";
import type { SymbolView } from "../../view/grid/SymbolView";
import { AlphaDimEffect } from "../symbol-dim/AlphaDimEffect";
import { HostedSpineLandEffect, HostedSpineRemovalEffect, HostedSpineWinEffect } from "./HostedSpineSymbolAdapters";
import { SymbolSpineHost, type SymbolSpineSource } from "./SymbolSpineHost";

/** A grid's Spine symbols and the effects that play through them, built as one (decision 46). */
export interface SymbolSpineEffects {
  /** One skeleton per cell; null when the title has no Spine symbols. */
  host: SymbolSpineHost | null;
  /** The winners' `win` clip; null without a host or with the clip off. */
  winEffect: HostedSpineWinEffect | null;
  /** The reveal's land clip (the skip set lands plain); null without a host or with the clip off. */
  landEffect: HostedSpineLandEffect | null;
  /** A removed cell's `out` clip; null without a host or with the clip off. A title may wrap it before the tumble takes it. */
  removalEffect: HostedSpineRemovalEffect | null;
  /** The win and feature dim, on the cell's content node, so a Spine covering the sprite dims with it. */
  dim: AlphaDimEffect;
  /**
   * The grid layer's bind hook: the sprite shown again (the host re-hides it if it attaches a skeleton), the cell's dim
   * released, then the cell hosted or un-hosted per the bind. A recycled cell that missed the release kept its dim.
   */
  onSymbolBound: (view: SymbolView, symbolId: string, opts?: SymbolBindOpts) => void;
  /** The grid layer's release hook: the host takes its skeleton back. */
  onSymbolReleased: (view: SymbolView) => void;
}

/**
 * The symbol-effects wiring every title with Spine symbols repeats: the host, the hosted win, land and removal
 * effects, the dim and the grid's bind and release hooks that keep them consistent (boundary review 2026-10-01 #13,
 * decision 46). A null clip turns its effect off; the sprite fallbacks are the title's.
 */
export function createSymbolSpineEffects(input: {
  /** symbolId → skeleton + seat; empty → no host. */
  sources: ReadonlyMap<string, SymbolSpineSource>;
  runner: PresentationTaskRunner;
  driver: SpineDriver;
  clips: { idle: string | null; land: string | null; win: string | null; out: string | null };
  /** Symbols that never land-animate: bound plain, and declined by the land effect. */
  landSkipSymbolIds?: readonly string[];
  /** Clips that are chrome, not round presentation — run at timeScale 1 (e.g. ["idle"]). */
  ambientClips?: readonly string[];
  clipMix?: SpineClipMixSpec | null;
}): SymbolSpineEffects {
  const { clips, runner } = input;
  const host =
    input.sources.size > 0
      ? new SymbolSpineHost({
          sourcesById: input.sources,
          taskRunner: runner,
          driver: input.driver,
          idleClipName: clips.idle,
          landClipName: clips.land,
          landSkipSymbolIds: input.landSkipSymbolIds ?? null,
          ambientClips: input.ambientClips ?? [],
          clipMix: input.clipMix ?? null,
        })
      : null;
  const dim = new AlphaDimEffect(runner, { dimNodeResolver: (sprite) => sprite.parent });
  return {
    host,
    winEffect: host && clips.win ? new HostedSpineWinEffect({ host, clipName: clips.win }) : null,
    landEffect: host && clips.land ? new HostedSpineLandEffect({ host, clipName: clips.land, skipSymbolIds: input.landSkipSymbolIds ?? null }) : null,
    removalEffect: host && clips.out ? new HostedSpineRemovalEffect({ host, clipName: clips.out, taskRunner: runner }) : null,
    dim,
    onSymbolBound: (view, symbolId, opts) => {
      const sprite = view.symbolSprite;
      if (sprite && !sprite.destroyed) sprite.visible = true;
      dim.release(view.symbolSprite);
      host?.onViewBound(view, symbolId, opts?.transient, opts?.pendingLand);
    },
    onSymbolReleased: (view) => {
      host?.detach(view);
    },
  };
}
